import React from "react";

import { Box, Button, TextField, Typography } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import clsx from "clsx";
import { Observer, observer } from "mobx-react-lite";

import FormElement from "../../store/FormElement";
import { useEditing } from "../utils/formElement";
import {
  StyledDeleteIcon,
  StyledDragIndicatorIcon,
  StyledEditIcon,
  StyledHorizontalDivider,
  StyledInfoIcon,
  useStyles,
} from "../utils/Icons";

type ShortTextArguments = {
  label: string;
  defaultText: string;
};

interface Props {
  element: FormElement<ShortTextArguments>;
}

const FormShortText: React.FC<Props> = observer(({ element }) => {
  const { classes } = useStyles();
  const editing = useEditing(element);

  return (
    <>
      {!editing.value ? (
        <>
          <Box className={classes.body}>
            <StyledDragIndicatorIcon
              className={clsx(["styled-drag-indicator"])}
            />
            <div className={classes.longTextInputContent}>
              <Typography
                variant="h6"
                color="textPrimary"
                style={{ marginBottom: 12 }}
              >
                {element.arguments.label || "Short input text label"}
              </Typography>
              <TextField placeholder={element.arguments.defaultText} disabled />
            </div>

            <div className={clsx([classes.editIcons, "edit-icons-container"])}>
              <StyledInfoIcon />
              <StyledEditIcon onClick={editing.toggle} />
              <StyledDeleteIcon onClick={() => element.remove()} />
            </div>
          </Box>
        </>
      ) : (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon />
          <Box className={classes.edit}>
            <Box className={classes.columnContent}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginBottom: 8 }}
              >
                Short text input label
              </Typography>
              <Observer>
                {() => (
                  <TextField
                    value={element.arguments.label}
                    onChange={(e) =>
                      element.setArgument("label", e.target.value)
                    }
                  />
                )}
              </Observer>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginBottom: 8 }}
              >
                Default text
              </Typography>
              <Observer>
                {() => (
                  <TextField
                    placeholder="Enter short input label"
                    fullWidth
                    value={element.arguments.defaultText}
                    onChange={(e) =>
                      element.setArgument("defaultText", e.target.value)
                    }
                  />
                )}
              </Observer>
              <StyledHorizontalDivider orientation="horizontal" />
              <div className={classes.editFooter}>
                <div className={classes.editButtons}>
                  <Observer>
                    {() => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            aria-label="Required"
                            checked={element.required}
                            onChange={(e) =>
                              element.setRequired(e.target.checked)
                            }
                            inputProps={{ "aria-label": "primary checkbox" }}
                          />
                        }
                        label="Required"
                      />
                    )}
                  </Observer>
                  <Button variant="outlined" onClick={editing.cancel}>
                    Cancel
                  </Button>
                  <Button
                    className={classes.saveChangesButton}
                    onClick={editing.stop}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </Box>
          </Box>
          <div className={classes.editIcons}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon />
          </div>
        </Box>
      )}
    </>
  );
});

export default FormShortText;
